// Merchant-reported delays, on the customer's tracking.
//
// A status that has not moved for a day is alarming to a sender 1,500 miles
// away who cannot phone the shop. "Road blocked — expect tomorrow" is not. This
// turns silence into information.
//
// Shows the full history, newest first, because a second delay for a different
// reason is a different story from the same one twice — and if the order later
// becomes a dispute, the sequence is the evidence.

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock } from 'lucide-react';
import { delays, type FulfillmentDelay } from '../../lib/api';

/** "2 hours ago" beats a timestamp when the question is "is this stale?". */
function ago(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function DelayNotice({ requestId }: { requestId: number }) {
  const q = useQuery({
    queryKey: ['requests', requestId, 'delays'],
    queryFn: () => delays.list(requestId),
    // A 403 means this viewer may not see the order at all; the surrounding
    // page already handles that, so stay quiet rather than retrying.
    retry: false,
  });

  const items: FulfillmentDelay[] = q.data?.delays ?? [];

  // No delay is the normal case and needs no explanation.
  if (items.length === 0) return null;

  const latest = items[0];

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
        {items.length > 1 ? `Delayed · ${items.length} updates` : 'Delayed'}
      </p>

      <p className="mt-1 text-[13px] font-semibold text-slate-900">{latest.label}</p>
      {latest.note && <p className="mt-0.5 text-[12.5px] text-slate-700">“{latest.note}”</p>}

      {latest.new_eta && (
        <p className="mt-1 inline-flex items-center gap-1.5 text-[12px] text-slate-600">
          <Clock className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
          Now expected {new Date(latest.new_eta).toLocaleString()}
        </p>
      )}

      <p className="mt-1 text-[11px] text-slate-500">
        Reported by the merchant {ago(latest.created_at)}
      </p>

      {/* Earlier delays stay visible: the sequence is the story, and it is the
          record if this order is disputed. */}
      {items.length > 1 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11.5px] font-semibold text-amber-800 hover:underline">
            Earlier updates
          </summary>
          <ul className="mt-1.5 space-y-1.5 border-l-2 border-amber-200 pl-2.5">
            {items.slice(1).map((d) => (
              <li key={d.id} className="text-[12px] text-slate-600">
                <span className="font-semibold text-slate-800">{d.label}</span>
                {d.note && <span> — “{d.note}”</span>}
                <span className="text-slate-400"> · {ago(d.created_at)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
